import React, { useState, useEffect } from 'react';
// // // import { useTranslation } from 'react-i18next';   // Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, FileText, CheckCircle, XCircle, Clock, Search, RefreshCw, Eye, Download,
  Shield, AlertTriangle, Settings, FileCheck, AlertCircle,
  Calendar,
  ArrowLeft, ArrowRight
} from 'lucide-react';
import { db, appId, auth } from '../firebase/firebaseConfig.jsx';
import {
  collection, query, orderBy, limit, getDocs, doc, updateDoc, deleteDoc, where, startAfter, getCountFromServer, writeBatch, addDoc, serverTimestamp, getDoc, setDoc
} from 'firebase/firestore';
import { ref, deleteObject, getDownloadURL, getBytes } from 'firebase/storage';
import { storage } from '../firebase/firebaseConfig.jsx';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';
import {
  extractTextFromFile,
  extractName,
  extractNrcNumber,
  extractPassportNumber,
  compareUserData
} from '../utils/ocrUtils.js';
import { getDateTimestamp } from '../utils/dateUtils.js';
import adminApi from '../api/adminApiClient.js';
import '../styles/admin-dashboard-gov.css';

const ITEMS_PER_PAGE = 20;

/** 사용자 앱 언어 → ko | my | en */
const normalizeAppLanguage = (raw) => {
  const s = String(raw || '')
    .trim()
    .toLowerCase();
  if (!s) return 'my';
  if (s.startsWith('ko') || s === 'korean' || s === 'kr') return 'ko';
  if (s.startsWith('en') || s === 'english') return 'en';
  if (
    s.startsWith('my') ||
    s === 'mm' ||
    s === 'burmese' ||
    s === 'myanmar' ||
    s === 'mya'
  ) {
    return 'my';
  }
  return 'my';
};

const API_LANGUAGE_CODE = {
  ko: 'korean',
  my: 'myanmar',
  en: 'english',
};

const KYC_PUSH_COPY = {
  approved: {
    ko: {
      title: 'KYC 승인 완료',
      body: '본인 인증이 승인되었습니다. 송금 등 서비스를 이용하실 수 있습니다.',
      action: '송금하기',
    },
    my: {
      title: 'KYC အတည်ပြု ပြီးပါပြီ',
      body: 'ကိုယ်ရေးအတည်ပြုချက် အတည်ပြုပြီးပါပြီ။ ငွေလွှဲ စသည့် ဝန်ဆောင်မှုများကို အသုံးပြုနိုင်ပါပြီ။',
      action: 'ငွေလွှဲရန်',
    },
    en: {
      title: 'KYC Approved',
      body: 'Your identity verification has been approved. You can now use remittance and other services.',
      action: 'Go to remittance',
    },
  },
  rejected: {
    ko: {
      title: 'KYC 거절 안내',
      bodyFallback:
        '본인 인증이 거절되었습니다. 앱에서 안내에 따라 서류를 다시 제출해 주세요.',
      bodyWithReason: (reason) => `본인 인증이 거절되었습니다. 사유: ${reason}`,
      action: 'KYC 다시 신청',
    },
    my: {
      title: 'KYC ငြင်းပယ်မှု အကြောင်းကြား',
      bodyFallback:
        'ကိုယ်ရေးအတည်ပြုချက် ငြင်းပယ်ခံရပါသည်။ အက်ပ်ရှိ လမ်းညွှန်အတိုင်း စာရွက်များကို ပြန်လည်တင်သွင်းပါ။',
      bodyWithReason: (reason) =>
        `ကိုယ်ရေးအတည်ပြုချက် ငြင်းပယ်ခံရပါသည်။ အကြောင်းရင်း: ${reason}`,
      action: 'KYC ပြန်လျှောက်ရန်',
    },
    en: {
      title: 'KYC Rejected',
      bodyFallback:
        'Your identity verification was rejected. Please resubmit your documents following the in-app guidance.',
      bodyWithReason: (reason) =>
        `Your identity verification was rejected. Reason: ${reason}`,
      action: 'Reapply for KYC',
    },
  },
};

const statusBadgeClass = (status) => {
  if (status === 'pending') return 'pending';
  if (status === 'approved') return 'approved';
  if (status === 'rejected') return 'rejected';
  return 'no_application';
};

const AdminKycPage = () => {
  // const { t } = useTranslation(); // Admin은 다국어 지원 불필요
  const { showToastMessage } = useToastMessage();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [kycApplications, setKycApplications] = useState([]);
  const [groupedApplications, setGroupedApplications] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedUserFiles, setSelectedUserFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);
  
  // OCR verification states
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [ocrResults, setOcrResults] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [statusUpdatingUserId, setStatusUpdatingUserId] = useState(null);
  
  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    no_application: 0
  });

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'no_application', label: 'No Application' },
  ];

  // Load data on component mount
  useEffect(() => {
    fetchKycApplications();
    loadStatistics();
  }, [filterStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchTerm]);

  // Group KYC applications by user (show only latest application)
  const groupApplicationsByUser = (applications) => {
    const grouped = {};
    
    // Find only the latest application for each user
    applications.forEach(app => {
      const userId = app.uid || app.userId;
      let userEmail = app.userEmail || app.email;
      let userName = app.userName || app.name;
      
      // 사용자 정보가 "Unknown"이거나 누락된 경우 개선된 표시
      if (!userEmail || userEmail === 'Unknown') {
        userEmail = `${userId}@kairos.local`;
      }
      
      if (!userName || userName === 'Unknown') {
        userName = userId;
      }
      
      if (!grouped[userId]) {
        grouped[userId] = {
          userId,
          userEmail,
          userName,
          applications: [app], // Store only latest application
          totalFiles: app.files ? app.files.length : 0,
          latestStatus: app.status || 'pending',
          latestSubmittedAt: app.submittedAt || app.createdAt,
          firstSubmittedAt: app.submittedAt || app.createdAt
        };
      } else {
        // For existing users, check if this is a newer application
        const currentLatest = grouped[userId].latestSubmittedAt;
        const newAppTime = new Date(app.submittedAt || app.createdAt);
        const currentTime = new Date(currentLatest);
        
        if (newAppTime > currentTime) {
          // Replace if this is a newer application
          grouped[userId] = {
            userId,
            userEmail,
            userName,
            applications: [app], // Store only latest application
            totalFiles: app.files ? app.files.length : 0,
            latestStatus: app.status || 'pending',
            latestSubmittedAt: app.submittedAt || app.createdAt,
            firstSubmittedAt: grouped[userId].firstSubmittedAt // Keep first submission date
          };
        }
      }
    });
    
    // Latest status for each user group is already set (status of latest application)
    // No additional processing needed
    
    return grouped;
  };

  // Fetch KYC applications from Firebase (fetch all applications and group by user)
  const fetchKycApplications = async () => {
    setLoading(true);
    try {
      const kycApplicationsRef = collection(db, 'artifacts', appId, 'kyc_applications');
      
      // Fetch all applications (remove status filtering)
      const q = query(
        kycApplicationsRef,
        orderBy('submittedAt', 'desc'),
        limit(500)
      );

      const snap = await getDocs(q);
      const applications = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
        submittedAt: doc.data().submittedAt?.toDate?.() || new Date(),
      }));

      // Sort on client side (resolve index issues)
      applications.sort((a, b) => {
        const aTime = getDateTimestamp(a.submittedAt) || getDateTimestamp(a.createdAt) || 0;
        const bTime = getDateTimestamp(b.submittedAt) || getDateTimestamp(b.createdAt) || 0;
        return bTime - aTime; // Latest first
      });

      setKycApplications(applications);
      
      // Group by user (latest application only)
      const grouped = groupApplicationsByUser(applications);
      
      // Apply status filtering on client side
      let filteredGrouped = {};
      
      if (filterStatus === 'no_application') {
        // For no_application, fetch all users and show those without KYC applications
        try {
          const usersQuery = query(collection(db, 'artifacts', appId, 'users'));
          const usersSnap = await getDocs(usersQuery);
          const allUsers = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Find users who don't have KYC applications
          const usersWithKyc = new Set(Object.keys(grouped));
          allUsers.forEach(user => {
            if (!usersWithKyc.has(user.id)) {
              filteredGrouped[user.id] = {
                userId: user.id,
                userEmail: user.email || `${user.id}@kairos.local`,
                userName: user.name || user.displayName || user.id,
                applications: [],
                totalFiles: 0,
                latestStatus: 'no_application',
                latestApplicationDate: null
              };
            }
          });
        } catch (error) {
          console.error('Error fetching users for no_application filter:', error);
        }
      } else {
        // For other statuses, filter existing grouped applications
        Object.keys(grouped).forEach(userId => {
          const userGroup = grouped[userId];
          if (userGroup.latestStatus === filterStatus) {
            filteredGrouped[userId] = userGroup;
          }
        });
      }
      
      setGroupedApplications(filteredGrouped);
      
    } catch (error) {
      console.error('KYC applications loading failed:', error);
      showToastMessage('KYC 신청서 로딩에 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load statistics (calculated by user - latest application only)
  const loadStatistics = async () => {
    try {
      const allQuery = query(
        collection(db, 'artifacts', appId, 'kyc_applications'),
        orderBy('submittedAt', 'desc'),
        limit(200)
      );
      const allSnap = await getDocs(allQuery);
      
      const applications = allSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
        submittedAt: doc.data().submittedAt?.toDate?.() || new Date(),
      }));

      // Calculate statistics by grouping users (latest application only)
      const grouped = groupApplicationsByUser(applications);
      const userGroups = Object.values(grouped);

      // Get all users to calculate no_application count
      const usersQuery = query(collection(db, 'artifacts', appId, 'users'));
      const usersSnap = await getDocs(usersQuery);
      const allUsers = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Find users who don't have KYC applications
      const usersWithKyc = new Set(Object.keys(grouped));
      const noApplicationCount = allUsers.filter(user => !usersWithKyc.has(user.id)).length;

      const stats = {
        total: allUsers.length, // Total number of users
        pending: userGroups.filter(user => user.latestStatus === 'pending').length,
        approved: userGroups.filter(user => user.latestStatus === 'approved').length,
        rejected: userGroups.filter(user => user.latestStatus === 'rejected').length,
        no_application: noApplicationCount
      };

      console.log('📊 KYC 통계 업데이트:', stats);
      setStats(stats);
    } catch (error) {
      console.error('Statistics loading failed:', error);
    }
  };

  // Handle status update (update only latest application)
  const handleStatusUpdate = async (applicationId, newStatus, userGroup = null, reason = '') => {
    // Model B: final KYC approve/reject decision is owned by Hecto (webhook H-07).
    // Admin may only reject incomplete local submissions.
    if (newStatus === 'approved') {
      showToastMessage(
        'Admin cannot approve KYC. Final approval comes from Hecto (webhook H-07). Use Resync to Hecto.',
        'warning'
      );
      return;
    }

    const targetUserId = userGroup?.userId || null;
    if (targetUserId) {
      setStatusUpdatingUserId(targetUserId);
    }
    try {
      console.log('🔄 상태 업데이트 시작:', { applicationId, newStatus, userGroup: userGroup?.userId, reason });
      const actor = auth.currentUser;
      const actorInfo = {
        uid: actor?.uid || null,
        email: actor?.email || null,
        displayName: actor?.displayName || null
      };
      
      // Delete uploaded files when rejected
      if (newStatus === 'rejected' && userGroup) {
        await deleteUploadedFiles(userGroup);
      }
      
      // Update only latest application (first application in user group)
      if (userGroup && userGroup.applications && userGroup.applications.length > 0) {
        const latestApp = userGroup.applications[0]; // Latest application
        console.log(`📝 최신 애플리케이션 상태 업데이트 중: ${latestApp.id}`);
        
        // 핵심 상태 변경은 우선 커밋 (이력 저장 실패가 전체 실패를 만들지 않도록 분리)
        const batch = writeBatch(db);
        const previousStatus = latestApp.status || 'pending';
        
        // Update only latest KYC application status
        const appRef = doc(db, 'artifacts', appId, 'kyc_applications', latestApp.id);
        batch.update(appRef, {
          status: newStatus,
          updatedAt: new Date(),
          reviewedBy: actorInfo.uid || actorInfo.email || 'admin',
          reviewedByEmail: actorInfo.email || null,
          reviewNotes: newStatus === 'rejected' ? reason : null
        });
        
        // 사용자의 KYC 상태도 함께 업데이트
        const userRef = doc(db, 'artifacts', appId, 'users', userGroup.userId);
        batch.update(userRef, {
          kycStatus: newStatus,
          kycUpdatedAt: new Date(),
          kycUpdatedBy: actorInfo.uid || actorInfo.email || 'admin',
          kycUpdatedByEmail: actorInfo.email || null,
          kycReason: newStatus === 'rejected' ? reason : null
        });

        // 상태 변경 커밋
        await batch.commit();
        console.log('✅ 상태 업데이트 완료');

        // 이력 저장은 별도 시도 (권한/스키마 이슈 시 상태 변경은 유지)
        try {
          const historyRef = doc(collection(db, 'artifacts', appId, 'kyc_status_history'));
          await setDoc(historyRef, {
            userId: userGroup.userId,
            applicationId: latestApp.id,
            previousStatus,
            newStatus,
            reason: reason || null,
            changedAt: serverTimestamp(),
            changedByUid: actorInfo.uid,
            changedByEmail: actorInfo.email,
            changedByName: actorInfo.displayName,
            source: 'admin_kyc_page'
          });
          console.log('✅ KYC 상태 변경 이력 저장 완료');
        } catch (historyError) {
          console.error('⚠️ KYC 상태 변경 이력 저장 실패(상태 변경은 성공):', historyError);
          showToastMessage('상태 변경은 완료되었지만 이력 저장 권한이 없습니다.', 'warning');
        }
        
      } else {
        // 단일 애플리케이션만 업데이트 (fallback)
        const batch = writeBatch(db);
        const appForHistory = kycApplications.find((item) => item.id === applicationId);
        const previousStatus = appForHistory?.status || 'unknown';
        const fallbackUserId = appForHistory?.uid || appForHistory?.userId || null;

        const appRef = doc(db, 'artifacts', appId, 'kyc_applications', applicationId);
        batch.update(appRef, {
          status: newStatus,
          updatedAt: new Date(),
          reviewedBy: actorInfo.uid || actorInfo.email || 'admin',
          reviewedByEmail: actorInfo.email || null,
          reviewNotes: newStatus === 'rejected' ? reason : null
        });

        await batch.commit();

        try {
          const historyRef = doc(collection(db, 'artifacts', appId, 'kyc_status_history'));
          await setDoc(historyRef, {
            userId: fallbackUserId,
            applicationId,
            previousStatus,
            newStatus,
            reason: reason || null,
            changedAt: serverTimestamp(),
            changedByUid: actorInfo.uid,
            changedByEmail: actorInfo.email,
            changedByName: actorInfo.displayName,
            source: 'admin_kyc_page_fallback'
          });
        } catch (historyError) {
          console.error('⚠️ fallback 이력 저장 실패(상태 변경은 성공):', historyError);
          showToastMessage('상태 변경은 완료되었지만 이력 저장 권한이 없습니다.', 'warning');
        }
      }
      
      // 사용자에게 푸시·인앱 알림 전송 (앱 언어 설정 반영)
      if (userGroup) {
        try {
          const userLang = await resolveUserAppLanguage(userGroup.userId);
          if (newStatus === 'approved') {
            // Model B: Admin must not approve. Final status comes from Hecto H-07 webhook.
            showToastMessage(
              'Admin approval is disabled. KYC is approved only via Hecto webhook (H-07). Use “Resync to Hecto” instead.',
              'warning'
            );
          } else if (newStatus === 'rejected') {
            const copy = KYC_PUSH_COPY.rejected[userLang] || KYC_PUSH_COPY.rejected.my;
            const body = reason?.trim()
              ? copy.bodyWithReason(reason.trim())
              : copy.bodyFallback;
            await writeKycInAppMessage(
              userGroup.userId,
              copy.title,
              body,
              'kyc_rejected',
              { reason: reason || null, action: copy.action }
            );
            await sendPushNotification(
              userGroup.userId,
              copy.title,
              body,
              { type: 'kyc_rejected', reason: reason },
              userLang
            );
          }
        } catch (pushError) {
          console.error('⚠️ 상태 변경 후 푸시알림 전송 실패:', pushError);
          showToastMessage('상태 변경은 완료되었지만 알림 전송에 실패했습니다', 'warning');
        }
      }
      
      const statusMessage = newStatus === 'rejected' 
        ? 'KYC 신청이 거부되었습니다. 업로드된 파일들이 삭제되었습니다.' 
        : `KYC 신청이 ${newStatus}로 변경되었습니다`;
      
      showToastMessage(statusMessage, 'success');
      
      // 거부 모달 닫기
      if (newStatus === 'rejected') {
        setIsRejectModalOpen(false);
        setRejectReason('');
        setSelectedUserForAction(null);
      }
      
      // 데이터 즉시 새로고침
      console.log('🔄 데이터 새로고침 시작...');
      await fetchKycApplications();
      await loadStatistics();
      console.log('✅ 데이터 새로고침 완료');
    } catch (error) {
      console.error('Status update failed:', error);
      showToastMessage('상태 변경에 실패했습니다', 'error');
    } finally {
      setStatusUpdatingUserId(null);
    }
  };

  // Handle reject with reason
  const handleRejectWithReason = (userGroup) => {
    const applications = Array.isArray(userGroup?.applications) ? userGroup.applications : [];
    if (applications.length === 0 || !applications[0]?.id) {
      showToastMessage('거절할 신청 데이터가 없습니다. 목록을 새로고침 후 다시 시도해주세요.', 'error');
      return;
    }

    setSelectedUserForAction(userGroup);
    setIsRejectModalOpen(true);
  };

  // Handle approve removed for Model B — Hecto decides.
  // "Resync to Hecto" retries package delivery without setting approved.
  const handleResyncToHecto = async (userGroup) => {
    const applications = Array.isArray(userGroup?.applications) ? userGroup.applications : [];
    if (applications.length === 0 || !applications[0]?.id || !userGroup?.userId) {
      showToastMessage('재전송할 신청 데이터가 없습니다.', 'error');
      return;
    }
    const confirmed = window.confirm(
      '이 신청을 헥토로 다시 전송할까요?\n(최종 승인/거절은 헥토 KYC 웹훅으로만 반영됩니다)'
    );
    if (!confirmed) return;

    setStatusUpdatingUserId(userGroup.userId);
    try {
      const syncRes = await adminApi.syncKycToHecto({
        userId: userGroup.userId,
        applicationId: applications[0].id,
        force: true,
      });
      const syncData = syncRes?.data || syncRes;
      if (syncData?.success) {
        showToastMessage(
          syncData.simAutoApproved
            ? 'Hecto sync OK (SIM auto-approved).'
            : syncData.isSimulation
              ? 'Hecto sync recorded (simulation). Waiting for decision webhook.'
              : 'Hecto sync OK. Waiting for Hecto KYC decision webhook.',
          'success'
        );
        await fetchKycApplications();
        await loadStatistics();
      } else {
        showToastMessage(
          `Hecto sync failed: ${syncData?.error || syncData?.code || 'unknown'}`,
          'error'
        );
      }
    } catch (syncError) {
      showToastMessage(
        `Hecto sync request failed: ${syncError.message || 'network'}`,
        'error'
      );
    } finally {
      setStatusUpdatingUserId(null);
    }
  };

  // Local reject only (incomplete docs) — does not call Hecto approve
  const handleApprove = (userGroup) => {
    handleResyncToHecto(userGroup);
  };

  // Handle application view
  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setIsModalOpen(true);
  };

  // Handle user files view
  const handleViewUserFiles = async (userGroup) => {
    const allFiles = [];
    userGroup.applications.forEach(app => {
      if (app.files && app.files.length > 0) {
        allFiles.push(...app.files.map(file => ({
          ...file,
          applicationId: app.id,
          submittedAt: app.submittedAt || app.createdAt
        })));
      }
    });
    setSelectedUserFiles(allFiles);
    setIsModalOpen(true);
    setCurrentUserId(userGroup.userId);
    
    // 사용자 프로필 데이터 로드
    try {
      const profileDoc = await getDoc(doc(db, `artifacts/${appId}/users/${userGroup.userId}/data/profile`));
      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        console.log('👤 사용자 프로필 로드 완료:', {
          name: profileData.name || profileData.fullName,
          nrcNumber: profileData.nrcNumber,
          passportNumber: profileData.passportNumber
        });
        setUserProfile(profileData);
      } else {
        console.warn('⚠️ 사용자 프로필이 존재하지 않음:', userGroup.userId);
        setUserProfile(null);
      }
    } catch (error) {
      console.error('❌ 사용자 프로필 로드 실패:', error);
      setUserProfile(null);
    }
    
    // OCR 결과 초기화
    setOcrResults(null);
    setVerificationStatus(null);
  };
  
  // OCR 검증 수행
  const handleOcrVerification = async () => {
    if (!selectedUserFiles || selectedUserFiles.length === 0) {
      showToastMessage('검증할 파일이 없습니다', 'error');
      return;
    }
    
    if (!userProfile) {
      showToastMessage('사용자 프로필 정보를 불러올 수 없습니다', 'error');
      return;
    }
    
    setIsOcrProcessing(true);
    setOcrResults(null);
    setVerificationStatus(null);
    
    try {
      // 모든 파일에서 텍스트 추출
      let allExtractedText = '';
      let processedFiles = 0;
      let failedFiles = 0;
      
      for (const file of selectedUserFiles) {
        if (file.type && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
          try {
            console.log(`📄 파일 처리 중: ${file.name}`, {
              type: file.type,
              url: file.url ? 'Present' : 'Missing',
              storagePath: file.storagePath ? file.storagePath : 'Missing'
            });
            // 백엔드 프록시를 통해 파일 다운로드 (CORS 우회)
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
            console.log('🔍 API Base URL:', apiBaseUrl);
            const text = await extractTextFromFile(
              file, 
              getDownloadURL, 
              ref, 
              storage, 
              getBytes,
              apiBaseUrl,
              getFirebaseToken
            );
            if (text && text.trim()) {
              allExtractedText += '\n' + text;
              processedFiles++;
              console.log(`✅ 파일 처리 완료: ${file.name} (${text.length}자)`);
            } else {
              console.warn(`⚠️ 파일에서 텍스트를 추출할 수 없음: ${file.name}`);
              failedFiles++;
            }
          } catch (error) {
            console.warn(`❌ 파일 ${file.name}에서 텍스트 추출 실패:`, error);
            failedFiles++;
          }
        } else {
          console.warn(`⚠️ 지원하지 않는 파일 형식: ${file.name} (${file.type})`);
        }
      }
      
      if (!allExtractedText.trim()) {
        showToastMessage(
          `문서에서 텍스트를 추출할 수 없습니다. (처리: ${processedFiles}, 실패: ${failedFiles})`, 
          'error'
        );
        setIsOcrProcessing(false);
        return;
      }
      
      console.log(`📊 전체 추출된 텍스트 길이: ${allExtractedText.length}자`);
      console.log(`📊 추출된 텍스트 전체:`, allExtractedText);
      console.log(`📊 추출된 텍스트 샘플 (처음 1000자):`, allExtractedText.substring(0, 1000));
      
      // 텍스트에서 정보 추출
      console.log('🔍 정보 추출 시작...');
      const extractedData = {
        name: extractName(allExtractedText),
        nrcNumber: extractNrcNumber(allExtractedText),
        passportNumber: extractPassportNumber(allExtractedText)
      };
      
      console.log('📄 추출된 데이터:', extractedData);
      console.log('📄 추출된 데이터 상세:', JSON.stringify(extractedData, null, 2));
      console.log('👤 사용자 프로필:', {
        name: userProfile.name || userProfile.fullName,
        nrcNumber: userProfile.nrcNumber,
        passportNumber: userProfile.passportNumber
      });
      
      // 사용자 프로필과 비교
      const comparison = compareUserData(userProfile, extractedData);
      
      console.log('🔍 비교 결과:', comparison);
      
      // 결과 저장
      const results = {
        extractedText: allExtractedText.substring(0, 1000), // 처음 1000자만 저장
        fullText: allExtractedText, // 전체 텍스트 저장 (디버깅 및 상세 정보용)
        extractedData,
        comparison,
        processedFiles,
        failedFiles
      };
      
      console.log('💾 OCR 결과 저장:', results);
      
      // React 상태 업데이트를 명시적으로 처리
      setOcrResults(results);
      
      // 검증 상태 설정
      let status = 'mismatch';
      let message = '❌ 정보가 일치하지 않습니다. 거부를 고려하세요.';
      
      if (comparison.overallMatch) {
        status = 'verified';
        message = '✅ 모든 정보가 일치합니다. 승인 가능합니다.';
      } else if (comparison.matchRate >= 66) {
        status = 'partial';
        message = '⚠️ 일부 정보가 일치합니다. 수동 검토가 필요합니다.';
      }
      
      console.log('📊 검증 상태 설정:', status);
      console.log('📊 ocrResults 상태:', results);
      console.log('📊 verificationStatus 상태:', status);
      
      // 상태를 동시에 업데이트
      setVerificationStatus(status);
      
      // 약간의 지연 후 토스트 메시지 표시 (상태 업데이트 보장)
      setTimeout(() => {
        showToastMessage(message, status === 'verified' ? 'success' : status === 'partial' ? 'warning' : 'error');
      }, 100);
      
    } catch (error) {
      console.error('❌ OCR 검증 오류:', error);
      console.error('❌ 오류 스택:', error.stack);
      
      // 에러가 발생해도 부분 결과를 표시
      setOcrResults({
        error: error.message || String(error),
        extractedText: '',
        extractedData: { name: null, nrcNumber: null, passportNumber: null },
        comparison: null,
        processedFiles: 0,
        failedFiles: 0
      });
      setVerificationStatus('error');
      showToastMessage('OCR 검증 중 오류가 발생했습니다: ' + (error.message || error), 'error');
    } finally {
      setIsOcrProcessing(false);
      console.log('🏁 OCR 처리 완료');
    }
  };

  const resolveFileUrl = async (file) => {
    if (!file) {
      showToastMessage('파일 정보가 없습니다', 'error');
      return null;
    }
    if (file.url) {
      return file.url;
    }
    if (!file.storagePath) {
      showToastMessage('파일 경로가 없습니다', 'error');
      return null;
    }

    try {
      const fileRef = ref(storage, file.storagePath);
      const url = await getDownloadURL(fileRef);
      return url;
    } catch (error) {
      console.error('파일 URL 생성 실패:', error);
      showToastMessage('파일 URL을 생성할 수 없습니다', 'error');
      return null;
    }
  };

  // Handle file preview
  const handlePreviewFile = async (file) => {
    const url = await resolveFileUrl(file);
    if (!url) return;
    setPreviewFile({ ...file, url });
    setIsPreviewModalOpen(true);
  };

  const handleDownloadFile = async (file) => {
    const url = await resolveFileUrl(file);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Check if file is previewable
  const isPreviewable = (file) => {
    const previewableTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf'
    ];
    return previewableTypes.includes(file.type);
  };

  // Get Firebase Auth token
  const getFirebaseToken = async () => {
    try {
      // 🔥 CRITICAL FIX: 정적 import 사용 (이미 상단에서 import됨)
      // auth는 상단에서 import되어 있음
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        return token;
      }
      return null;
    } catch (error) {
      console.error('Firebase token 가져오기 실패:', error);
      return null;
    }
  };

  /** 앱 사용자 language / preferredLanguage / settings 조회 */
  const resolveUserAppLanguage = async (userId) => {
    if (!userId) return 'my';
    try {
      const userRef = doc(db, 'artifacts', appId, 'users', userId);
      const [snap, settingsSnap] = await Promise.all([
        getDoc(userRef),
        getDoc(doc(db, 'artifacts', appId, 'users', userId, 'settings', 'language')),
      ]);
      const settingsLang = settingsSnap.exists()
        ? settingsSnap.data()?.language || settingsSnap.data()?.preferredLanguage
        : null;
      const d = snap.exists() ? snap.data() || {} : {};
      return normalizeAppLanguage(
        settingsLang || d.language || d.preferredLanguage || d.locale
      );
    } catch (err) {
      console.warn('사용자 언어 조회 실패, my 기본값 사용:', err?.message);
      return 'my';
    }
  };

  /** RCC 승인과 동일: 앱 인앱 토스트 경로(users/{uid}/in_app_messages) */
  const writeKycInAppMessage = async (userId, title, body, type, extra = {}) => {
    if (!userId) return;
    try {
      const defaultAction =
        type === 'kyc_rejected' ? 'KYC 다시 신청' : '송금하기';
      await addDoc(collection(db, 'artifacts', appId, 'users', userId, 'in_app_messages'), {
        title,
        body,
        type: type === 'kyc_rejected' ? 'error' : 'success',
        category: type,
        isRead: false,
        isClicked: false,
        createdAt: serverTimestamp(),
        action: extra.action || defaultAction,
        actionUrl: type === 'kyc_rejected' ? '/kyc' : '/remittance',
        duration: 0,
        persistent: true,
        ...extra,
      });
      console.log('✅ KYC 인앱 메시지 저장:', userId, type);
    } catch (inAppErr) {
      console.error('⚠️ KYC 인앱 메시지 저장 실패:', inAppErr?.code || inAppErr?.message);
    }
  };

  // Send push notification
  const sendPushNotification = async (userId, title, body, data = {}, appLang = 'my') => {
    try {
      console.log('🔔 푸시알림 전송 시작:', { userId, title, body, data, appLang });
      console.log('🔍 환경 변수 확인:', {
        VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
        appId: appId
      });
      
      // 1. Firestore에 알림 저장
      const notificationData = {
        title: title,
        body: body,
        type: data.type || 'kyc_status',
        targetType: 'specific_users', // 특정 사용자에게만 보내는 알림
        targetUsers: [userId],
        targetCount: 1, // 대상 사용자 수
        read: false,
        createdAt: serverTimestamp(),
        createdBy: 'admin',
        language: appLang,
        data: data
      };
      
      console.log('📝 Firestore 알림 데이터:', notificationData);
      const docRef = await addDoc(collection(db, 'artifacts', appId, 'notifications'), notificationData);
      console.log('✅ Firestore에 알림 저장 완료, 문서 ID:', docRef.id);
      
      // 2. 백엔드 API를 통해 푸시알림 전송 (AdminNotificationPage와 동일한 방식)
      const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/notifications/send`;
      console.log('🌐 API URL:', apiUrl);
      
      const payload = {
        userIds: [userId], // 배열로 전송
        title: title,
        message: body, // 원래대로 message 필드 사용
        type: data.type || 'kyc_status',
        language: API_LANGUAGE_CODE[appLang] || 'myanmar',
        sender: 'admin',  // 발송자 정보 추가
        senderName: 'Kairos'  // 발송자 이름 추가
      };
      console.log('📦 전송 데이터:', payload);
      
      // 백엔드 API를 통해 푸시알림 전송
      console.log('🚀 API 요청 시작...');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        mode: 'cors', // 🔥 CORS 모드 명시
        credentials: 'include' // 🔥 인증 정보 포함
      });
      
      console.log('📡 응답 상태:', response.status);
      console.log('📡 응답 헤더:', Object.fromEntries(response.headers.entries()));
      
      const responseData = await response.json();
      console.log('📡 응답 내용:', responseData);
      
      if (response.ok && responseData.success) {
        console.log('✅ 푸시알림 전송 성공:', responseData);
        
        // 푸시알림 발송 시간 기록 (sentAt 필드)
        await updateDoc(docRef, {
          sentAt: serverTimestamp(),
          successCount: responseData.successCount || 1
        });
        console.log('✅ 푸시알림 발송 시간 기록 완료');
        
        // 성공 메시지 개선
        const successMessage = responseData.successCount > 0 
          ? `푸시알림이 전송되었습니다 (성공: ${responseData.successCount}건)`
          : `푸시알림이 전송되었습니다 (대상 사용자에게 FCM 토큰이 등록되지 않음)`;
        showToastMessage(successMessage, 'success');
      } else {
        console.error('❌ 푸시알림 전송 실패:', response.status, responseData);
        
        // 오류 메시지 개선
        let errorMessage = '푸시알림 전송에 실패했습니다';
        if (responseData.error) {
          if (responseData.error.includes('No valid FCM tokens')) {
            errorMessage = '대상 사용자에게 FCM 토큰이 등록되지 않았습니다. 사용자가 알림 권한을 허용해야 합니다.';
          } else {
            errorMessage = `푸시알림 전송 실패: ${responseData.error}`;
          }
        }
        showToastMessage(errorMessage, 'warning'); // error 대신 warning으로 변경
      }
    } catch (error) {
      console.error('❌ 푸시알림 전송 오류:', error);
      console.error('❌ 오류 스택:', error.stack);
      showToastMessage(`푸시알림 전송 중 오류가 발생했습니다: ${error.message}`, 'error');
    }
  };

  // Delete uploaded files from Firebase Storage (최신 신청의 파일만)
  const deleteUploadedFiles = async (userGroup) => {
    try {
      const deletePromises = [];
      
      // 최신 신청의 파일만 삭제
      if (userGroup.applications && userGroup.applications.length > 0) {
        const latestApp = userGroup.applications[0]; // Latest application
        if (latestApp.files && latestApp.files.length > 0) {
          latestApp.files.forEach(file => {
            if (file.storagePath) {
              // Firebase Storage에서 파일 삭제
              const fileRef = ref(storage, file.storagePath);
              deletePromises.push(deleteObject(fileRef));
            }
          });
        }
      }
      
      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
        console.log(`✅ ${deletePromises.length}개 파일이 Firebase Storage에서 삭제되었습니다.`);
      }
    } catch (error) {
      console.error('❌ 파일 삭제 실패:', error);
      // 파일 삭제 실패해도 계속 진행 (Firestore 문서는 삭제됨)
    }
  };

  // Handle application delete
  const handleDeleteApplication = async (applicationId) => {
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'kyc_applications', applicationId));
      showToastMessage('KYC 신청서가 삭제되었습니다', 'success');
      fetchKycApplications();
      loadStatistics();
    } catch (error) {
      console.error('Delete failed:', error);
      showToastMessage('삭제에 실패했습니다', 'error');
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle,
      no_application: AlertCircle
    };
    return icons[status] || Clock;
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'no_application': return 'No Application';
      default: return 'Unknown';
    }
  };

  // Filter data based on search criteria
  const getFilteredData = (data) => {
    return data.filter(item => {
      const matchesSearch = !searchTerm || 
        item.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.id?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  };

  const filteredRows = getFilteredData(Object.values(groupedApplications));
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pagedRows = filteredRows.slice(
    (safeCurrentPage - 1) * ITEMS_PER_PAGE,
    safeCurrentPage * ITEMS_PER_PAGE
  );

  const formatSubmittedDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
    } catch (error) {
      console.error('Date conversion error:', error, value);
      return value.toString();
    }
  };

  return (
    <div className="admin-gov">
      <div className="adg-shell">
        <motion.header
          className="adg-header"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div>
            <div className="adg-gold-line" />
            <div className="adg-brand">
              <div className="adg-kpi-icon">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h1>KYC 관리</h1>
                <p>신원 인증 신청 심사 · 수동 새로고침</p>
              </div>
            </div>
          </div>
          <div className="adg-header-meta">
            <span className="adg-chip">{filteredRows.length} users</span>
            <button
              type="button"
              onClick={fetchKycApplications}
              disabled={loading}
              className="adg-btn adg-btn-primary"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              새로고침
            </button>
          </div>
        </motion.header>

        <motion.section
          className="adg-kpi-grid"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
        >
          <article className="adg-kpi">
            <div className="adg-kpi-top">
              <div>
                <div className="adg-kpi-label">전체 사용자</div>
                <div className="adg-kpi-sub">KYC 대상</div>
              </div>
              <div className="adg-kpi-icon"><Users className="w-5 h-5" /></div>
            </div>
            <div className="adg-kpi-value">{stats.total}</div>
          </article>
          <article className="adg-kpi">
            <div className="adg-kpi-top">
              <div>
                <div className="adg-kpi-label">심사대기</div>
                <div className="adg-kpi-sub">Pending review</div>
              </div>
              <div className="adg-kpi-icon"><Clock className="w-5 h-5" /></div>
            </div>
            <div className="adg-kpi-value">{stats.pending}</div>
          </article>
          <article className="adg-kpi">
            <div className="adg-kpi-top">
              <div>
                <div className="adg-kpi-label">승인</div>
                <div className="adg-kpi-sub">Approved</div>
              </div>
              <div className="adg-kpi-icon"><CheckCircle className="w-5 h-5" /></div>
            </div>
            <div className="adg-kpi-value">{stats.approved}</div>
          </article>
          <article className="adg-kpi">
            <div className="adg-kpi-top">
              <div>
                <div className="adg-kpi-label">거절</div>
                <div className="adg-kpi-sub">Rejected</div>
              </div>
              <div className="adg-kpi-icon"><XCircle className="w-5 h-5" /></div>
            </div>
            <div className="adg-kpi-value">{stats.rejected}</div>
          </article>
        </motion.section>

        <motion.section
          className="adg-panel"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
        >
          <div className="adg-toolbar">
            <div className="adg-search">
              <Search />
              <input
                type="text"
                className="adg-input"
                placeholder="이름, 이메일, ID 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="adg-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setCurrentPage(1);
                fetchKycApplications();
              }}
              disabled={loading}
              className="adg-btn adg-btn-primary"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              조회
            </button>
          </div>

          <div className="adg-table-wrap">
            {loading ? (
              <div className="adg-loading">
                <div className="adg-spinner" />
                <p>KYC 신청 목록 불러오는 중</p>
              </div>
            ) : Object.keys(groupedApplications).length === 0 ? (
              <div className="adg-empty">
                <Shield className="w-10 h-10" style={{ color: 'var(--text-muted)' }} />
                <h3>신청 목록이 없습니다</h3>
                <p>사용자가 인증 신청하면 목록에 표시됩니다. 새로고침으로 확인하세요.</p>
              </div>
            ) : (
              <table className="adg-table">
                <thead>
                  <tr>
                    <th><span className="th-inner"><Users className="w-3.5 h-3.5" /> User</span></th>
                    <th><span className="th-inner"><FileText className="w-3.5 h-3.5" /> Documents</span></th>
                    <th><span className="th-inner"><FileCheck className="w-3.5 h-3.5" /> Applications</span></th>
                    <th><span className="th-inner"><Shield className="w-3.5 h-3.5" /> Status</span></th>
                    <th><span className="th-inner"><Calendar className="w-3.5 h-3.5" /> Updated</span></th>
                    <th><span className="th-inner"><Settings className="w-3.5 h-3.5" /> Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((userGroup, index) => {
                    const StatusIcon = getStatusIcon(userGroup.latestStatus);
                    return (
                      <motion.tr
                        key={userGroup.userId}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(index * 0.03, 0.3) }}
                      >
                        <td>
                          <div className="adg-user-cell">
                            <div className="adg-avatar"><Users className="w-4 h-4" /></div>
                            <div>
                              <div className="name">{userGroup.userName}</div>
                              <div className="email">{userGroup.userEmail}</div>
                              <div className="uid">ID: {userGroup.userId.substring(0, 8)}…</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="adg-pill">
                            <FileText className="w-3 h-3" />
                            {userGroup.totalFiles} files
                          </span>
                        </td>
                        <td>
                          <span className="adg-pill">
                            <FileCheck className="w-3 h-3" />
                            {userGroup.applications.length} apps
                          </span>
                        </td>
                        <td>
                          <span className={`adg-status ${statusBadgeClass(userGroup.latestStatus)}`}>
                            <StatusIcon className="w-3 h-3" />
                            {getStatusText(userGroup.latestStatus)}
                          </span>
                        </td>
                        <td>
                          <span className="adg-pill">
                            <Calendar className="w-3 h-3" />
                            {formatSubmittedDate(userGroup.latestSubmittedAt)}
                          </span>
                        </td>
                        <td>
                          <div className="adg-actions">
                            <button
                              type="button"
                              onClick={() => handleViewUserFiles(userGroup)}
                              className="adg-btn"
                              title="View uploaded files"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                            {userGroup.latestStatus === 'pending' && (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleApprove(userGroup);
                                  }}
                                  onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleApprove(userGroup);
                                  }}
                                  disabled={
                                    statusUpdatingUserId === userGroup.userId ||
                                    (currentUserId === userGroup.userId && verificationStatus === 'mismatch')
                                  }
                                  className="adg-btn adg-btn-primary adg-btn-icon"
                                  title={
                                    statusUpdatingUserId === userGroup.userId
                                      ? '처리 중...'
                                      : 'Resync to Hecto (final approve is Hecto webhook)'
                                  }
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRejectWithReason(userGroup);
                                  }}
                                  onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRejectWithReason(userGroup);
                                  }}
                                  disabled={statusUpdatingUserId === userGroup.userId}
                                  className="adg-btn adg-btn-danger adg-btn-icon"
                                  title={statusUpdatingUserId === userGroup.userId ? '상태 변경 처리 중...' : 'Reject'}
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && (
            <div className="adg-pagination">
              <div>
                Page {safeCurrentPage} of {totalPages} · {filteredRows.length} users
              </div>
              <div className="pager">
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
                  disabled={safeCurrentPage === 1}
                  className="adg-btn"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
                  disabled={safeCurrentPage === totalPages}
                  className="adg-btn"
                >
                  Next
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </motion.section>
      </div>

      <AnimatePresence>
        {isModalOpen && (selectedApplication || selectedUserFiles.length > 0) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="adg-modal-backdrop"
            onClick={() => {
              setIsModalOpen(false);
              setSelectedApplication(null);
              setSelectedUserFiles([]);
              setOcrResults(null);
              setVerificationStatus(null);
              setUserProfile(null);
              setCurrentUserId(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="adg-modal lg"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedUserFiles.length > 0 ? (
                <div>
                  <div className="adg-modal-head">
                    <div>
                      <h3>Uploaded Files</h3>
                      <p>문서 미리보기 · OCR 검증</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleOcrVerification}
                      disabled={isOcrProcessing || !userProfile}
                      className="adg-btn adg-btn-primary"
                    >
                      {isOcrProcessing ? (
                        <>
                          <div className="adg-spinner" style={{ width: 14, height: 14, margin: 0, borderWidth: 2 }} />
                          OCR 처리 중...
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4" />
                          OCR 검증
                        </>
                      )}
                    </button>
                  </div>

                  {isOcrProcessing && (
                    <div className="adg-alert info">OCR 처리 중... 잠시만 기다려주세요.</div>
                  )}

                  {ocrResults && (
                    <div
                      className={`adg-alert ${
                        verificationStatus === 'verified'
                          ? 'success'
                          : verificationStatus === 'partial'
                          ? 'warn'
                          : verificationStatus === 'error'
                          ? 'neutral'
                          : 'danger'
                      }`}
                    >
                      <div className="adg-actions" style={{ marginBottom: 10 }}>
                        {verificationStatus === 'verified' ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : verificationStatus === 'partial' ? (
                          <AlertTriangle className="w-5 h-5" />
                        ) : verificationStatus === 'error' ? (
                          <AlertCircle className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <strong>
                          {verificationStatus === 'verified'
                            ? '검증 완료'
                            : verificationStatus === 'partial'
                            ? '부분 일치'
                            : verificationStatus === 'error'
                            ? '처리 오류'
                            : '불일치'}
                        </strong>
                      </div>

                      {ocrResults.error && (
                        <div className="adg-alert danger" style={{ marginBottom: 10 }}>
                          <strong>오류:</strong> {ocrResults.error}
                        </div>
                      )}

                      {ocrResults.comparison ? (
                        <div>
                          <div className="adg-compare-row">
                            <span>이름</span>
                            <div className="vals">
                              <span>{ocrResults.comparison.name.userValue || 'N/A'}</span>
                              <span style={{ color: 'var(--text-muted)' }}>vs</span>
                              <span>{ocrResults.comparison.name.extractedValue || 'N/A'}</span>
                              {ocrResults.comparison.name.match ? (
                                <CheckCircle className="w-4 h-4" style={{ color: 'var(--emerald)' }} />
                              ) : (
                                <XCircle className="w-4 h-4" style={{ color: 'var(--status-failed)' }} />
                              )}
                            </div>
                          </div>
                          <div className="adg-compare-row">
                            <span>NRC 번호</span>
                            <div className="vals">
                              <span>{ocrResults.comparison.nrcNumber.userValue || 'N/A'}</span>
                              <span style={{ color: 'var(--text-muted)' }}>vs</span>
                              <span>{ocrResults.comparison.nrcNumber.extractedValue || 'N/A'}</span>
                              {ocrResults.comparison.nrcNumber.match ? (
                                <CheckCircle className="w-4 h-4" style={{ color: 'var(--emerald)' }} />
                              ) : (
                                <XCircle className="w-4 h-4" style={{ color: 'var(--status-failed)' }} />
                              )}
                            </div>
                          </div>
                          <div className="adg-compare-row">
                            <span>여권 번호</span>
                            <div className="vals">
                              <span>{ocrResults.comparison.passportNumber.userValue || 'N/A'}</span>
                              <span style={{ color: 'var(--text-muted)' }}>vs</span>
                              <span>{ocrResults.comparison.passportNumber.extractedValue || 'N/A'}</span>
                              {ocrResults.comparison.passportNumber.match ? (
                                <CheckCircle className="w-4 h-4" style={{ color: 'var(--emerald)' }} />
                              ) : (
                                <XCircle className="w-4 h-4" style={{ color: 'var(--status-failed)' }} />
                              )}
                            </div>
                          </div>
                          <div className="adg-compare-row" style={{ marginTop: 8, borderTop: '1px solid var(--bg-border)', paddingTop: 10 }}>
                            <span>일치율</span>
                            <strong style={{ color: 'var(--gold-bright)', fontFamily: 'var(--font-mono)' }}>
                              {ocrResults.comparison.matchRate.toFixed(0)}%
                            </strong>
                          </div>
                          {ocrResults.processedFiles !== undefined && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
                              처리된 파일: {ocrResults.processedFiles}개
                              {ocrResults.failedFiles > 0 && `, 실패: ${ocrResults.failedFiles}개`}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          {ocrResults.extractedData ? (
                            <div>
                              <p><strong>추출된 정보</strong></p>
                              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                                <li>이름: {ocrResults.extractedData.name || '추출 실패'}</li>
                                <li>NRC 번호: {ocrResults.extractedData.nrcNumber || '추출 실패'}</li>
                                <li>여권 번호: {ocrResults.extractedData.passportNumber || '추출 실패'}</li>
                              </ul>
                            </div>
                          ) : (
                            <p>텍스트 추출은 완료되었지만 정보를 파싱할 수 없습니다.</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {ocrResults && (
                    <details className="adg-details">
                      <summary>상세 정보 보기</summary>
                      <div>
                        <strong style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>추출된 텍스트 (처음 500자)</strong>
                        <pre>
                          {ocrResults.fullText
                            ? ocrResults.fullText.substring(0, 500)
                            : ocrResults.extractedText
                            ? ocrResults.extractedText.substring(0, 500)
                            : '텍스트 없음'}
                        </pre>
                        {ocrResults.fullText && (
                          <>
                            <strong style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>전체 추출된 텍스트</strong>
                            <pre>{ocrResults.fullText}</pre>
                          </>
                        )}
                        <strong style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>추출된 데이터</strong>
                        <pre>{JSON.stringify(ocrResults.extractedData, null, 2)}</pre>
                        {ocrResults.comparison && (
                          <>
                            <strong style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>비교 결과</strong>
                            <pre>{JSON.stringify(ocrResults.comparison, null, 2)}</pre>
                          </>
                        )}
                        <strong style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>처리 통계</strong>
                        <pre>
                          {JSON.stringify(
                            {
                              processedFiles: ocrResults.processedFiles,
                              failedFiles: ocrResults.failedFiles,
                              verificationStatus,
                            },
                            null,
                            2
                          )}
                        </pre>
                      </div>
                    </details>
                  )}

                  <div style={{ marginTop: 16 }}>
                    {selectedUserFiles.map((file, index) => (
                      <div key={index} className="adg-file-row">
                        <div className="adg-user-cell">
                          <div className="adg-avatar"><FileText className="w-4 h-4" /></div>
                          <div>
                            <div className="name">{file.name}</div>
                            <div className="meta">
                              {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Size unknown'} ·{' '}
                              {file.submittedAt ? new Date(file.submittedAt).toLocaleString() : 'Date unknown'}
                            </div>
                          </div>
                        </div>
                        <div className="adg-actions">
                          {isPreviewable(file) && (
                            <button type="button" onClick={() => handlePreviewFile(file)} className="adg-btn adg-btn-icon adg-btn-success" title="Preview">
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button type="button" onClick={() => handleDownloadFile(file)} className="adg-btn adg-btn-icon" title="Download">
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="adg-modal-head">
                    <div>
                      <h3>KYC Application Details</h3>
                      <p>신청서 상세 정보</p>
                    </div>
                  </div>
                  <div className="adg-field">
                    <label>User Name</label>
                    <p>{selectedApplication?.userName || 'N/A'}</p>
                  </div>
                  <div className="adg-field">
                    <label>Email</label>
                    <p>{selectedApplication?.userEmail || 'N/A'}</p>
                  </div>
                  <div className="adg-field">
                    <label>Application Date</label>
                    <p>
                      {selectedApplication?.createdAt
                        ? new Date(selectedApplication.createdAt).toLocaleString()
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="adg-field">
                    <label>Status</label>
                    <p>{getStatusText(selectedApplication?.status)}</p>
                  </div>
                </div>
              )}
              <div className="adg-modal-foot">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedApplication(null);
                    setSelectedUserFiles([]);
                    setOcrResults(null);
                    setVerificationStatus(null);
                    setUserProfile(null);
                    setCurrentUserId(null);
                  }}
                  className="adg-btn"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {isPreviewModalOpen && previewFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="adg-modal-backdrop"
            onClick={() => {
              setIsPreviewModalOpen(false);
              setPreviewFile(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="adg-modal lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="adg-modal-head">
                <div>
                  <h3>File Preview</h3>
                  <p>{previewFile.name}</p>
                </div>
                <button
                  type="button"
                  className="adg-btn adg-btn-icon adg-btn-ghost"
                  onClick={() => {
                    setIsPreviewModalOpen(false);
                    setPreviewFile(null);
                  }}
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="adg-file-row" style={{ display: 'block' }}>
                {previewFile.type.startsWith('image/') ? (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={previewFile.url}
                      alt={previewFile.name}
                      style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 10 }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div style={{ display: 'none' }} className="adg-alert danger">
                      Unable to load image.
                    </div>
                  </div>
                ) : previewFile.type === 'application/pdf' ? (
                  <div>
                    <iframe
                      src={previewFile.url}
                      style={{ width: '100%', height: '60vh', border: 0, borderRadius: 10 }}
                      title={previewFile.name}
                    />
                    <p className="meta" style={{ marginTop: 8 }}>
                      PDF가 보이지 않으면{' '}
                      <a href={previewFile.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold-bright)' }}>
                        새 탭에서 열기
                      </a>
                    </p>
                  </div>
                ) : (
                  <div className="adg-empty" style={{ padding: 24 }}>
                    <FileText className="w-12 h-12" style={{ color: 'var(--text-muted)' }} />
                    <p>이 파일 형식은 미리보기를 지원하지 않습니다.</p>
                    <a href={previewFile.url} target="_blank" rel="noopener noreferrer" className="adg-btn adg-btn-primary" style={{ marginTop: 12, textDecoration: 'none' }}>
                      <Download className="w-4 h-4" />
                      Download File
                    </a>
                  </div>
                )}
              </div>

              <div className="adg-pagination" style={{ marginTop: 14, borderRadius: 8 }}>
                <div>Size: {previewFile.size ? `${(previewFile.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown'}</div>
                <div>
                  Upload: {previewFile.submittedAt ? new Date(previewFile.submittedAt).toLocaleString() : 'Unknown'}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {isRejectModalOpen && selectedUserForAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="adg-modal-backdrop"
            onClick={() => {
              setIsRejectModalOpen(false);
              setRejectReason('');
              setSelectedUserForAction(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="adg-modal sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="adg-modal-head">
                <div>
                  <h3>KYC 거절</h3>
                  <p>
                    {selectedUserForAction.userName} ({selectedUserForAction.userEmail})
                  </p>
                </div>
              </div>
              <div className="adg-alert warn">
                거절 시 업로드된 파일이 삭제되며, 사용자는 새 문서로 재신청할 수 있습니다.
              </div>
              <div className="adg-field">
                <label>거절 사유</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="예: 문서가 불명확함, 추가 서류 필요 등"
                  className="adg-textarea"
                  rows={4}
                  required
                />
              </div>
              <div className="adg-modal-foot">
                <button
                  type="button"
                  onClick={() => {
                    setIsRejectModalOpen(false);
                    setRejectReason('');
                    setSelectedUserForAction(null);
                  }}
                  className="adg-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (rejectReason.trim()) {
                      console.log('❌ 거부 처리:', selectedUserForAction.userId, selectedUserForAction.applications.length + '개 애플리케이션');
                      const latestApplication = selectedUserForAction.applications[0];
                      handleStatusUpdate(latestApplication.id, 'rejected', selectedUserForAction, rejectReason);
                    } else {
                      showToastMessage('Please enter a rejection reason', 'error');
                    }
                  }}
                  className="adg-btn adg-btn-danger"
                >
                  Reject
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminKycPage;